import { LoadingIndicator } from '@vrplatform/components-legacy';
import { type ComponentProps, useMemo } from 'react';
import { Select, type SelectDataProps } from '../select';
import type { SelectValueProps } from '../select/_types';
import { FilterPill } from './FilterPill';
import { pluralize } from '@vrplatform/ui-common';
import type { SelectItem } from '../select/_types';

type Props<TSelectValue extends string> = {
  label: string;
  multiLabel?: string;
  popoverWidth?: number;
  popoverHeight?: number;
  withinPortal?: boolean;
  loadingMode?: 'indicator' | 'icon';
  searchPlaceholder?: string;
  hideClearButton?: boolean;
  hideSearch?: boolean;
  hideIcon?: boolean;
  noOptionsText?: string;
  pinnedItems?: SelectItem[];
  customBottomActions?: ComponentProps<typeof Select>['customBottomActions'];
  width?: number;
  filterType?: ComponentProps<typeof FilterPill>['type'];
  onOpenChange?: ComponentProps<typeof Select>['onOpenChange'];
} & SelectDataProps<TSelectValue> &
  SelectValueProps<TSelectValue>;

export const SelectFilter = <TSelectValue extends string>({
  setValue,
  value,
  label: filterLabel,
  withinPortal = true,
  multiLabel,
  popoverWidth = 250,
  // popoverHeight,
  loadingMode,
  searchPlaceholder,
  hideClearButton,
  hideSearch,
  hideIcon,
  noOptionsText,
  customBottomActions,
  width,
  filterType,
  onOpenChange,
  ...dataProps
}: Props<TSelectValue>) => {
  return (
    <Select
      {...dataProps}
      setValue={setValue as any}
      value={value as any}
      onOpenChange={onOpenChange}
      inputProps={{
        width,
        loadingQuery:
          dataProps.data?.loading ||
          (dataProps.infiniteData?.isFetching && !dataProps.infiniteData?.data),
      }}
      dropdownProps={{
        position: 'bottom-start',
        preventCloseOnSelect: true,
        width: popoverWidth,
        // height: popoverHeight,
        withinPortal,
        searchPlaceholder,
        hideSearch,
        noOptionsText,
      }}
      customBottomActions={customBottomActions}
    >
      {({
        value: v,
        loadingQuery,
        opened,
        type,
        setValue,
        handlers,
        width,
      }) => {
        const label = useMemo(() => {
          if (type === 'multiple') {
            const value = v as SelectItem[] | null;

            let label = filterLabel;

            if (value?.length === 1) label = value[0].label;
            else if (value?.length)
              label = `${value.length} ${pluralize(value.length, multiLabel || filterLabel.toLowerCase())}`;

            return label;
          } else if (type === 'single') {
            const value = v as SelectItem | null;
            if (!value) return filterLabel;
            return value.label;
          }
        }, [v, filterLabel, type, multiLabel]);

        const isDefault = label === filterLabel;

        if (loadingQuery && loadingMode === 'indicator')
          return <LoadingIndicator size="xs" pt={2} />;

        return (
          <FilterPill
            opened={opened}
            isDefault={isDefault}
            type={filterType}
            loading={loadingMode === 'icon' ? loadingQuery : undefined}
            icon={
              type === 'single' ? !hideIcon && (value as any)?.icon : undefined
            }
            label={label || ''}
            width={width}
            resetFilter={
              hideClearButton
                ? undefined
                : () => {
                    handlers.close();

                    type === 'single'
                      ? setValue(null, { type: 'removed', value: v! })
                      : setValue([], { type: 'removed', value: v[0] });
                  }
            }
          />
        );
      }}
    </Select>
  );
};
